import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  Clock3,
  Edit3,
  Filter,
  ListMusic,
  Loader2,
  Music2,
  Plus,
  RefreshCw,
  Save,
  Search,
  ShieldCheck,
  Trash2,
  UserRound,
  X,
  XCircle,
} from "lucide-react";
import { PaginationMeta } from "../../types/api";
import { getCurrentTokenAccessInfo } from "../../utils/jwt";
import { fetchDashboard } from "../dashboard/dashboardApi";
import { fetchSongsV2, SongV2ListItem } from "../songV2/songV2Api";
import {
  CreateSongRequestData,
  createSongRequest,
  deleteSongRequest,
  fetchAllSongRequestsPaged,
  fetchSongRequest,
  SongRequest,
  SongRequestPriority,
  SongRequestPushResult,
  SongRequestSortKey,
  SongRequestStatus,
  updateSongRequest,
  updateSongRequestStatus,
} from "./songRequestApi";
import styles from "./SongRequestShared.module.css";

const PAGE_SIZE = 20;
const dateFormatter = new Intl.DateTimeFormat("tr-TR", {
  dateStyle: "medium",
  timeStyle: "short",
});

const statusLabels: Record<SongRequestStatus, string> = {
  pending: "Bekliyor",
  in_progress: "İşlemde",
  completed: "Tamamlandı",
  rejected: "Reddedildi",
};

const statusIcons = {
  pending: Clock3,
  in_progress: Loader2,
  completed: CheckCircle2,
  rejected: XCircle,
};

const priorityLabels: Record<SongRequestPriority, string> = {
  low: "Düşük",
  normal: "Normal",
  high: "Yüksek",
  urgent: "Acil",
};

type RequestForm = {
  user_id: string;
  user_email: string;
  user_name: string;
  artist_name: string;
  song_title: string;
  song_link: string;
  album_name: string;
  genre: string;
  release_year: string;
  language: string;
  description: string;
  status: SongRequestStatus;
  priority: SongRequestPriority;
};

const emptyForm: RequestForm = {
  user_id: "",
  user_email: "",
  user_name: "",
  artist_name: "",
  song_title: "",
  song_link: "",
  album_name: "",
  genre: "",
  release_year: "",
  language: "",
  description: "",
  status: SongRequestStatus.PENDING,
  priority: "normal",
};

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date);
}

function requestToForm(item: SongRequest): RequestForm {
  return {
    user_id: item.user_id,
    user_email: item.user_email,
    user_name: item.user_name,
    artist_name: item.artist_name,
    song_title: item.song_title,
    song_link: item.song_link || "",
    album_name: item.album_name || "",
    genre: item.genre || "",
    release_year: item.release_year == null ? "" : String(item.release_year),
    language: item.language || "",
    description: item.description || "",
    status: item.status,
    priority: item.priority,
  };
}

export default function SongRequestList() {
  const access = getCurrentTokenAccessInfo();
  const canWrite = Boolean(access?.hasPanelAccess);
  const canDelete = Boolean(access?.canDeleteSongRequests);
  const [items, setItems] = useState<SongRequest[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [globalCounts, setGlobalCounts] = useState<{
    total: number;
    pending: number;
    inProgress: number;
    completed: number;
    rejected: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<"all" | SongRequestStatus>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [sortBy, setSortBy] = useState<SongRequestSortKey>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<SongRequest | null>(null);
  const [form, setForm] = useState<RequestForm>(emptyForm);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<SongRequest | null>(null);
  const [statusTarget, setStatusTarget] = useState<SongRequest | null>(null);
  const [nextStatus, setNextStatus] = useState<SongRequestStatus>(
    SongRequestStatus.IN_PROGRESS
  );
  const [catalog, setCatalog] = useState<SongV2ListItem[]>([]);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [completedSongId, setCompletedSongId] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [pushResult, setPushResult] = useState<SongRequestPushResult | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SongRequest | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const loadItems = useCallback(
    async (refresh = false) => {
      try {
        refresh ? setRefreshing(true) : setLoading(true);
        setError("");
        const response = await fetchAllSongRequestsPaged({
          page,
          limit: PAGE_SIZE,
          status: status === "all" ? undefined : status,
          user_id: userFilter.trim() || undefined,
          search: search || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
        });
        setItems(response.items.filter((item) => !item.is_deleted));
        setMeta(response.meta);
      } catch (err: any) {
        setError(err?.message || "Şarkı istekleri yüklenemedi.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [page, search, sortBy, sortOrder, status, userFilter]
  );

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    setPage(1);
  }, [sortBy, sortOrder, status, userFilter]);

  const loadDashboardCounts = useCallback(() => {
    fetchDashboard()
      .then((dashboard) =>
        setGlobalCounts({
          total: dashboard.song_requests.total,
          pending: dashboard.song_requests.pending,
          inProgress: dashboard.song_requests.in_progress,
          completed: dashboard.song_requests.completed,
          rejected: dashboard.song_requests.rejected,
        })
      )
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadDashboardCounts();
  }, [loadDashboardCounts]);

  const pageCounts = useMemo(
    () => ({
      total: Number(meta?.total ?? items.length),
      pending: items.filter((item) => item.status === "pending").length,
      inProgress: items.filter((item) => item.status === "in_progress").length,
      completed: items.filter((item) => item.status === "completed").length,
      rejected: items.filter((item) => item.status === "rejected").length,
    }),
    [items, meta]
  );
  const counts = globalCounts || pageCounts;
  const total = Number(meta?.total ?? counts.total);
  const totalPages = Math.max(1, Number(meta?.total_pages || 1));
  const currentPage = Number(meta?.page || page);
  const hasFilters =
    Boolean(searchInput || userFilter) || status !== "all";

  const updateForm = <K extends keyof RequestForm>(
    key: K,
    value: RequestForm[K]
  ) => setForm((current) => ({ ...current, [key]: value }));

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setEditorOpen(true);
    setError("");
  };

  const openEdit = (item: SongRequest) => {
    setEditing(item);
    setForm(requestToForm(item));
    setEditorOpen(true);
    setError("");
  };

  const submitEditor = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canWrite) return;
    if (
      !form.user_id.trim() ||
      !form.user_email.trim() ||
      !form.user_name.trim() ||
      !form.artist_name.trim() ||
      !form.song_title.trim()
    ) {
      setError("Kullanıcı ID, e-posta, kullanıcı adı, sanatçı ve şarkı adı zorunludur.");
      return;
    }
    const releaseYear = form.release_year.trim()
      ? Number(form.release_year)
      : null;
    if (
      releaseYear !== null &&
      (!Number.isInteger(releaseYear) || releaseYear < 1000 || releaseYear > 9999)
    ) {
      setError("Yayın yılı 4 haneli geçerli bir yıl olmalıdır.");
      return;
    }
    const canonicalData = {
      user_email: form.user_email.trim(),
      user_name: form.user_name.trim(),
      artist_name: form.artist_name.trim(),
      song_title: form.song_title.trim(),
      song_link: form.song_link.trim() || null,
      album_name: form.album_name.trim() || null,
      genre: form.genre.trim() || null,
      release_year: releaseYear,
      language: form.language.trim() || null,
      description: form.description.trim() || null,
      status: form.status,
      priority: form.priority,
    };
    try {
      setSaving(true);
      setError("");
      if (editing) {
        await updateSongRequest(editing.request_id, {
          ...canonicalData,
        });
        setMessage("Şarkı isteği güncellendi.");
      } else {
        const payload: CreateSongRequestData = {
          user_id: form.user_id.trim(),
          ...canonicalData,
        };
        await createSongRequest(payload);
        setMessage("Yeni şarkı isteği oluşturuldu.");
      }
      setEditorOpen(false);
      setEditing(null);
      await loadItems(true);
      loadDashboardCounts();
    } catch (err: any) {
      setError(err?.message || "Şarkı isteği kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (item: SongRequest) => {
    setDetail(item);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      setDetail(await fetchSongRequest(item.request_id));
    } catch (err: any) {
      setError(err?.message || "İstek detayı yüklenemedi.");
    } finally {
      setDetailLoading(false);
    }
  };

  const openStatus = async (
    item: SongRequest,
    targetStatus: SongRequestStatus
  ) => {
    setStatusTarget(item);
    setNextStatus(targetStatus);
    setCompletedSongId(item.completed_song_id || "");
    setAdminNotes(item.admin_notes || "");
    setPushResult(null);
    setCatalogSearch("");
    setError("");
    if (targetStatus === SongRequestStatus.COMPLETED && catalog.length === 0) {
      try {
        const response = await fetchSongsV2({ limit: 100 });
        setCatalog(response.data);
      } catch (err: any) {
        setError(err?.message || "V2 şarkı kataloğu yüklenemedi.");
      }
    }
  };

  const visibleCatalog = useMemo(() => {
    const query = catalogSearch.trim().toLocaleLowerCase("tr-TR");
    if (!query) return catalog;
    return catalog.filter((song) =>
      [song.title, song.artist, song.songv2_id].some((value) =>
        value.toLocaleLowerCase("tr-TR").includes(query)
      )
    );
  }, [catalog, catalogSearch]);

  const submitStatus = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!statusTarget || !canWrite) return;
    if (
      nextStatus === SongRequestStatus.COMPLETED &&
      !completedSongId.trim()
    ) {
      setError("Tamamlanan istek için katalogdan bir V2 şarkı seçin.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const result = await updateSongRequestStatus(
        statusTarget.request_id,
        nextStatus,
        nextStatus === SongRequestStatus.COMPLETED
          ? completedSongId
          : null,
        adminNotes.trim() || null
      );
      const updated = result.request;
      setPushResult(result.push);
      setItems((current) =>
        current.map((item) =>
          item.request_id === updated.request_id ? updated : item
        )
      );
      setDetail((current) =>
        current?.request_id === updated.request_id ? updated : current
      );
      if (!result.push) {
        setMessage(`İstek durumu “${statusLabels[nextStatus]}” olarak güncellendi.`);
      } else if (result.push.push_status === "sent") {
        setMessage(`Durum güncellendi ve push bildirimi ${result.push.sent} cihaza gönderildi.`);
      } else {
        setMessage(
          `Durum güncellendi; push gönderilmedi (${result.push.reason || result.push.push_status}).`
        );
      }
      setStatusTarget(null);
      loadDashboardCounts();
    } catch (err: any) {
      setError(err?.message || "Durum güncellenemedi.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget || !canDelete) return;
    try {
      setSaving(true);
      await deleteSongRequest(deleteTarget.request_id);
      setDeleteTarget(null);
      setDetailOpen(false);
      setMessage("Şarkı isteği soft-delete ile arşivlendi.");
      await loadItems(true);
      loadDashboardCounts();
    } catch (err: any) {
      setError(err?.message || "Şarkı isteği silinemedi.");
    } finally {
      setSaving(false);
    }
  };

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setUserFilter("");
    setStatus("all");
  };

  return (
    <div className={styles.page}>
      <header className={styles.hero}>
        <div>
          <span><ListMusic size={14} /> Talep merkezi</span>
          <h1>Şarkı İstekleri</h1>
          <p>Kullanıcı taleplerini tüm metadata ve öncelik bilgileriyle yönetin; tamamlanan isteği V2 katalog kaydıyla ilişkilendirin.</p>
        </div>
        <div>
          <button type="button" onClick={() => loadItems(true)} disabled={refreshing}><RefreshCw size={16} className={refreshing ? styles.spinning : ""} /> Yenile</button>
          {canWrite && <button type="button" className={styles.primaryAction} onClick={openCreate}><Plus size={17} /> Yeni istek</button>}
        </div>
      </header>

      <section className={styles.stats}>
        <article><span className={styles.statBlue}><ListMusic size={18} /></span><div><strong>{counts.total}</strong><small>Toplam istek</small></div></article>
        <article><span className={styles.statAmber}><Clock3 size={18} /></span><div><strong>{counts.pending}</strong><small>Bekliyor</small></div></article>
        <article><span className={styles.statViolet}><Loader2 size={18} /></span><div><strong>{counts.inProgress}</strong><small>İşlemde</small></div></article>
        <article><span className={styles.statGreen}><CheckCircle2 size={18} /></span><div><strong>{counts.completed}</strong><small>Tamamlandı</small></div></article>
        <article><span className={styles.statRed}><XCircle size={18} /></span><div><strong>{counts.rejected}</strong><small>Reddedildi</small></div></article>
      </section>

      {(error || message) && (
        <div className={error ? styles.feedbackError : styles.feedbackSuccess}>
          {error ? <AlertCircle size={17} /> : <ShieldCheck size={17} />}<span>{error || message}</span>
          <button type="button" onClick={() => { setError(""); setMessage(""); }}><X size={15} /></button>
        </div>
      )}

      {pushResult && (
        <section className={`${styles.pushResult} ${styles[`push_${pushResult.push_status}`]}`}>
          <div>
            {pushResult.push_status === "sent" ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
            <span><strong>Push: {pushResult.push_status}</strong><small>{pushResult.reason || "Teslimat sağlayıcısı sonucu"}</small></span>
          </div>
          <dl>
            <div><dt>Hedef cihaz</dt><dd>{pushResult.targeted_devices}</dd></div>
            <div><dt>Gönderildi</dt><dd>{pushResult.sent}</dd></div>
            <div><dt>Başarısız</dt><dd>{pushResult.failed}</dd></div>
            <div><dt>Atlandı</dt><dd>{pushResult.skipped}</dd></div>
          </dl>
          <button type="button" onClick={() => setPushResult(null)} aria-label="Push sonucunu kapat"><X size={15} /></button>
        </section>
      )}

      <section className={styles.directory}>
        <div className={styles.directoryHeader}><div><span>İş kuyruğu</span><h2>İstek listesi</h2></div><span>{total.toLocaleString("tr-TR")} kayıt</span></div>
        <div className={styles.toolbar}>
          <label className={styles.searchBox}><Search size={16} /><input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Şarkı, sanatçı veya açıklama ara..." />{searchInput && <button type="button" onClick={() => setSearchInput("")}><X size={14} /></button>}</label>
          <label><Filter size={14} /><select value={status} onChange={(e) => setStatus(e.target.value as "all" | SongRequestStatus)}><option value="all">Tüm durumlar</option>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label><UserRound size={14} /><input value={userFilter} onChange={(e) => setUserFilter(e.target.value)} placeholder="Kullanıcı ID" /></label>
          <label><ArrowDown size={14} /><select value={sortBy} onChange={(e) => setSortBy(e.target.value as SongRequestSortKey)}><option value="created_at">Oluşturulma</option><option value="updated_at">Güncellenme</option><option value="status">Durum</option><option value="priority">Öncelik</option><option value="song_title">Şarkı adı</option><option value="artist_name">Sanatçı</option></select></label>
          <button type="button" className={styles.sortButton} onClick={() => setSortOrder((current) => current === "asc" ? "desc" : "asc")}>{sortOrder === "asc" ? <ArrowUp size={16} /> : <ArrowDown size={16} />}</button>
        </div>
        {hasFilters && <div className={styles.resultLine}><span>Filtrelenmiş sonuçlar</span><button type="button" onClick={resetFilters}>Filtreleri temizle</button></div>}

        {loading ? (
          <div className={styles.state}><div className={styles.loader} /><h3>İstekler hazırlanıyor</h3><p>Güncel iş kuyruğu yükleniyor.</p></div>
        ) : items.length === 0 ? (
          <div className={styles.state}><ListMusic size={32} /><h3>İstek bulunamadı</h3><p>Filtreleri değiştirin veya yeni bir istek oluşturun.</p></div>
        ) : (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead><tr><th>İstek</th><th>Kullanıcı ID</th><th>Durum / Öncelik</th><th>Talep sahibi</th><th>Güncellendi</th><th /></tr></thead>
                <tbody>
                  {items.map((item) => {
                    const StatusIcon = statusIcons[item.status];
                    return (
                      <tr key={item.request_id} onClick={() => openDetail(item)}>
                        <td><div className={styles.requestIdentity}><span><Music2 size={18} /></span><div><strong>{item.song_title}</strong><p>{item.artist_name || "Sanatçı belirtilmemiş"}</p><small>{item.request_id}</small></div></div></td>
                        <td><span className={styles.userCell}><UserRound size={13} />{item.user_id}</span></td>
                        <td><div className={styles.stateStack}><span className={`${styles.status} ${styles[`status_${item.status}`]}`}><StatusIcon size={12} />{statusLabels[item.status]}</span><span className={`${styles.priority} ${styles[`priority_${item.priority}`]}`}>{priorityLabels[item.priority]}</span></div></td>
                        <td><div className={styles.userCell}><strong>{item.user_name || "—"}</strong><small>{item.user_email || item.user_id}</small></div></td>
                        <td>{formatDate(item.updated_at)}</td>
                        <td><div className={styles.rowActions}>
                          {canWrite && item.status !== SongRequestStatus.COMPLETED && <button type="button" title="Durumu değiştir" onClick={(e) => { e.stopPropagation(); openStatus(item, item.status === SongRequestStatus.PENDING ? SongRequestStatus.IN_PROGRESS : SongRequestStatus.COMPLETED); }}><CircleDot size={15} /></button>}
                          {canWrite && <button type="button" title="Düzenle" onClick={(e) => { e.stopPropagation(); openEdit(item); }}><Edit3 size={15} /></button>}
                          {canDelete && <button type="button" className={styles.danger} title="Sil" onClick={(e) => { e.stopPropagation(); setDeleteTarget(item); }}><Trash2 size={15} /></button>}
                          <button type="button" aria-label="Detayı aç"><ArrowRight size={16} /></button>
                        </div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className={styles.mobileGrid}>
              {items.map((item) => {
                const StatusIcon = statusIcons[item.status];
                return <button type="button" key={item.request_id} onClick={() => openDetail(item)}><div><span><Music2 size={17} /></span><div><strong>{item.song_title}</strong><p>{item.artist_name || "Sanatçı yok"}</p></div><ArrowRight size={16} /></div><section><span className={`${styles.status} ${styles[`status_${item.status}`]}`}><StatusIcon size={12} />{statusLabels[item.status]}</span><span className={`${styles.priority} ${styles[`priority_${item.priority}`]}`}>{priorityLabels[item.priority]}</span><span><UserRound size={12} />{item.user_name || item.user_id}</span></section><small>{formatDate(item.updated_at)}</small></button>;
              })}
            </div>
          </>
        )}
      </section>

      {totalPages > 1 && <nav className={styles.pagination}><span>{total} kayıttan {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, total)}</span><div><button type="button" disabled={currentPage <= 1 || loading} onClick={() => setPage((current) => current - 1)}><ChevronLeft size={16} /> Önceki</button><span>Sayfa {currentPage} / {totalPages}</span><button type="button" disabled={currentPage >= totalPages || loading} onClick={() => setPage((current) => current + 1)}>Sonraki <ChevronRight size={16} /></button></div></nav>}

      {editorOpen && (
        <div className={styles.overlay} role="presentation" onMouseDown={() => !saving && setEditorOpen(false)}>
          <section className={styles.modal} role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <header><span><Edit3 size={19} /></span><div><small>{editing ? "İsteği düzenle" : "Yeni kayıt"}</small><h2>{editing ? editing.song_title : "Şarkı isteği oluştur"}</h2></div><button type="button" onClick={() => setEditorOpen(false)}><X size={17} /></button></header>
            <form onSubmit={submitEditor}>
              <div className={styles.formGrid}>
                <label><span>Kullanıcı ID *</span><input value={form.user_id} disabled={Boolean(editing)} maxLength={200} onChange={(e) => updateForm("user_id", e.target.value)} required /></label>
                <label><span>Kullanıcı e-postası *</span><input type="email" value={form.user_email} maxLength={320} onChange={(e) => updateForm("user_email", e.target.value)} required /></label>
                <label><span>Kullanıcı adı *</span><input value={form.user_name} maxLength={200} onChange={(e) => updateForm("user_name", e.target.value)} required /></label>
                <label><span>Şarkı adı *</span><input value={form.song_title} maxLength={200} onChange={(e) => updateForm("song_title", e.target.value)} required /></label>
                <label><span>Sanatçı *</span><input value={form.artist_name} maxLength={200} onChange={(e) => updateForm("artist_name", e.target.value)} required /></label>
                <label><span>Şarkı bağlantısı</span><input type="url" value={form.song_link} maxLength={1000} onChange={(e) => updateForm("song_link", e.target.value)} placeholder="https://youtu.be/..." /></label>
                <label><span>Albüm</span><input value={form.album_name} maxLength={200} onChange={(e) => updateForm("album_name", e.target.value)} /></label>
                <label><span>Tür</span><input value={form.genre} maxLength={100} onChange={(e) => updateForm("genre", e.target.value)} placeholder="rock" /></label>
                <label><span>Yayın yılı</span><input type="number" min={1000} max={9999} value={form.release_year} onChange={(e) => updateForm("release_year", e.target.value)} /></label>
                <label><span>Dil</span><input value={form.language} maxLength={100} onChange={(e) => updateForm("language", e.target.value)} placeholder="Türkçe" /></label>
                <label><span>Durum</span><select value={form.status} onChange={(e) => updateForm("status", e.target.value as SongRequestStatus)}>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                <label><span>Öncelik</span><select value={form.priority} onChange={(e) => updateForm("priority", e.target.value as SongRequestPriority)}>{Object.entries(priorityLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                <label className={styles.fullField}><span>Açıklama <small>{form.description.length}/2000</small></span><textarea rows={5} value={form.description} maxLength={2000} onChange={(e) => updateForm("description", e.target.value)} placeholder="Studio versiyonu..." /></label>
              </div>
              <footer><button type="button" onClick={() => setEditorOpen(false)}>İptal</button><button type="submit" disabled={saving}><Save size={16} />{saving ? "Kaydediliyor" : editing ? "Değişiklikleri kaydet" : "İsteği oluştur"}</button></footer>
            </form>
          </section>
        </div>
      )}

      {detailOpen && detail && (
        <div className={styles.overlay} role="presentation" onMouseDown={() => setDetailOpen(false)}>
          <aside className={styles.drawer} role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <header><div><small>İSTEK DETAYI</small><h2>{detail.song_title}</h2><p>{detail.artist_name || "Sanatçı belirtilmemiş"}</p></div><button type="button" onClick={() => setDetailOpen(false)}><X size={18} /></button></header>
            {detailLoading ? <div className={styles.state}><div className={styles.loader} /></div> : (
              <>
                <div className={styles.drawerStatus}><span className={`${styles.status} ${styles[`status_${detail.status}`]}`}>{statusLabels[detail.status]}</span><span className={`${styles.priority} ${styles[`priority_${detail.priority}`]}`}>{priorityLabels[detail.priority]}</span><small>{detail.request_id}</small></div>
                <div className={styles.detailGrid}>
                  <div><span>Mongo ID</span><strong>{detail._id || "—"}</strong></div>
                  <div><span>Kullanıcı ID</span><strong>{detail.user_id}</strong></div>
                  <div><span>Kullanıcı</span><strong>{detail.user_name || "—"}</strong></div>
                  <div><span>E-posta</span><strong>{detail.user_email || "—"}</strong></div>
                  <div><span>İşleyen admin</span><strong>{detail.processed_by || "—"}</strong></div>
                  <div><span>Albüm</span><strong>{detail.album_name || "—"}</strong></div>
                  <div><span>Tür</span><strong>{detail.genre || "—"}</strong></div>
                  <div><span>Yayın yılı</span><strong>{detail.release_year || "—"}</strong></div>
                  <div><span>Dil</span><strong>{detail.language || "—"}</strong></div>
                  <div><span>Oluşturuldu</span><strong>{formatDate(detail.created_at)}</strong></div>
                  <div><span>Güncellendi</span><strong>{formatDate(detail.updated_at)}</strong></div>
                  <div><span>Tahmini tamamlanma</span><strong>{formatDate(detail.estimated_completion)}</strong></div>
                  <div><span>Completed song ID</span><strong>{detail.completed_song_id || "—"}</strong></div>
                  <div><span>Soft-delete</span><strong>{detail.is_deleted ? "Evet" : "Hayır"}</strong></div>
                  <div><span>Silinme tarihi</span><strong>{formatDate(detail.deleted_at)}</strong></div>
                </div>
                {detail.song_link && <a className={styles.songLink} href={detail.song_link} target="_blank" rel="noopener noreferrer"><Music2 size={16} /><div><small>ŞARKI BAĞLANTISI</small><strong>{detail.song_link}</strong></div><ArrowRight size={16} /></a>}
                <section className={styles.notes}><span>Açıklama</span><p>{detail.description || "Açıklama eklenmemiş."}</p></section>
                <section className={styles.notes}><span>Admin notları</span><p>{detail.admin_notes || "Admin notu eklenmemiş."}</p></section>
                {detail.completed_song_id && <Link className={styles.songLink} to={`/songs/${detail.completed_song_id}`}><Music2 size={16} /><div><small>BAĞLI V2 ŞARKI</small><strong>{detail.completed_song_id}</strong></div><ArrowRight size={16} /></Link>}
                <div className={styles.timeline}>
                  {Object.values(SongRequestStatus).map((step) => <div key={step} className={detail.status === step ? styles.timelineActive : ""}><i /><span>{statusLabels[step]}</span></div>)}
                </div>
                <footer>
                  {canWrite && <button type="button" onClick={() => { setDetailOpen(false); openEdit(detail); }}><Edit3 size={15} /> Düzenle</button>}
                  {canWrite && <button type="button" onClick={() => openStatus(detail, detail.status === SongRequestStatus.PENDING ? SongRequestStatus.IN_PROGRESS : SongRequestStatus.COMPLETED)}><CircleDot size={15} /> Durumu değiştir</button>}
                  {canDelete && <button type="button" className={styles.deleteButton} onClick={() => setDeleteTarget(detail)}><Trash2 size={15} /> Sil</button>}
                </footer>
              </>
            )}
          </aside>
        </div>
      )}

      {statusTarget && (
        <div className={styles.overlay} role="presentation" onMouseDown={() => !saving && setStatusTarget(null)}>
          <section className={`${styles.modal} ${styles.statusModal}`} role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <header><span><CircleDot size={19} /></span><div><small>DURUM GEÇİŞİ</small><h2>{statusTarget.song_title}</h2></div><button type="button" onClick={() => setStatusTarget(null)}><X size={17} /></button></header>
            <form onSubmit={submitStatus}>
              <div className={styles.statusOptions}>
                {Object.values(SongRequestStatus).map((value) => {
                  const Icon = statusIcons[value];
                  return <button type="button" key={value} className={nextStatus === value ? styles.statusOptionActive : ""} onClick={() => { setNextStatus(value); if (value === SongRequestStatus.COMPLETED && catalog.length === 0) fetchSongsV2({ limit: 100 }).then((r) => setCatalog(r.data)).catch(() => {}); }}><Icon size={16} />{statusLabels[value]}</button>;
                })}
              </div>
              {nextStatus === SongRequestStatus.COMPLETED && (
                <div className={styles.catalogPicker}>
                  <label><span><Music2 size={14} /> Completed song ID *</span><input value={completedSongId} onChange={(e) => setCompletedSongId(e.target.value)} placeholder="songv2_id zorunludur" required /></label>
                  <label><span><Search size={14} /> V2 katalogda ara</span><input value={catalogSearch} onChange={(e) => setCatalogSearch(e.target.value)} placeholder="Şarkı, sanatçı veya songv2_id..." /></label>
                  <div>{visibleCatalog.map((song) => <button type="button" key={song.songv2_id} className={completedSongId === song.songv2_id ? styles.catalogSelected : ""} onClick={() => setCompletedSongId(song.songv2_id)}><span><Music2 size={15} /></span><div><strong>{song.title}</strong><small>{song.artist} · {song.songv2_id}</small></div>{completedSongId === song.songv2_id && <CheckCircle2 size={16} />}</button>)}</div>
                </div>
              )}
              <label className={styles.adminNotesField}><span>Admin notları</span><textarea rows={4} value={adminNotes} maxLength={2000} onChange={(e) => setAdminNotes(e.target.value)} placeholder="Durum değişikliği ve kullanıcıya iletilecek açıklama..." /></label>
              <footer><button type="button" onClick={() => setStatusTarget(null)}>İptal</button><button type="submit" disabled={saving}><Save size={16} />{saving ? "Güncelleniyor" : "Durumu kaydet"}</button></footer>
            </form>
          </section>
        </div>
      )}

      {deleteTarget && (
        <div className={styles.overlay} role="presentation" onMouseDown={() => !saving && setDeleteTarget(null)}>
          <section className={`${styles.modal} ${styles.deleteModal}`} role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <span><Trash2 size={22} /></span><h2>İstek arşivlensin mi?</h2><p><strong>{deleteTarget.song_title}</strong> soft-delete ile normal listeden kaldırılacak; veritabanından fiziksel olarak silinmeyecek.</p>
            <div><button type="button" onClick={() => setDeleteTarget(null)} disabled={saving}>Vazgeç</button><button type="button" onClick={remove} disabled={saving}><Trash2 size={15} />{saving ? "Arşivleniyor" : "İsteği arşivle"}</button></div>
          </section>
        </div>
      )}
    </div>
  );
}
